import { ChangeDetectionStrategy, Component, booleanAttribute, computed, effect, inject, input } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { RenderIconPipe } from '../pipes/render-icon.pipe';
import { NotoIconsService } from '../services/noto-icons.service';
import { isNotoRef, notoNameOf } from '../functions/icon-ref';

/**
 * Font Awesome's own size tokens as em multipliers, so the Noto branch matches the
 * box an `<fa-icon size="...">` would occupy at the same call site.
 */
const FA_SIZE_EM: Record<string, number> = {
  '2xs': 0.625,
  xs: 0.75,
  sm: 0.875,
  lg: 1.25,
  xl: 1.5,
  '2xl': 2,
  '1x': 1,
  '2x': 2,
  '3x': 3,
  '4x': 4,
  '5x': 5,
  '6x': 6,
  '7x': 7,
  '8x': 8,
  '9x': 9,
  '10x': 10,
};

/**
 * Renders a reflection question icon from whichever library its ref names.
 *
 * A bare ref is a Font Awesome Pro thin icon and goes down exactly the path it always
 * has -- the `renderIcon` pipe into `<fa-icon>` -- so colour continues to come from
 * the host's inherited `color` and every existing call site keeps its own treatment.
 * A `noto:` ref renders inline SVG instead, which carries its own palette and cannot
 * be tinted; `dimmed` is how those express an unselected state.
 */
@Component({
  selector: 'goatsports-icon',
  imports: [FaIconComponent, RenderIconPipe],
  templateUrl: './icon.component.html',
  styleUrl: './icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent {
  private readonly notoIcons = inject(NotoIconsService);

  /** Opaque ref as stored in Question.IconNames. */
  readonly ref = input.required<string | undefined>();

  /** Font Awesome size token, mirrored onto the Noto branch. */
  readonly size = input<SizeProp>();

  /**
   * Unselected treatment. Affects the Noto branch only -- Font Awesome icons are
   * monotone, so their call sites dim them by setting `color` as they always have.
   */
  readonly dimmed = input(false, { transform: booleanAttribute });

  protected readonly isNoto = computed(() => isNotoRef(this.ref()));

  /** `renderIcon` takes a definite string; an empty ref simply resolves to nothing. */
  protected readonly faRef = computed(() => this.ref() ?? '');

  protected readonly notoSvg = computed(() => {
    const ref = this.ref();
    return ref && isNotoRef(ref) ? this.notoIcons.svg(notoNameOf(ref)) : null;
  });

  protected readonly scale = computed(() => FA_SIZE_EM[this.size() ?? '1x'] ?? 1);

  constructor() {
    // Kicks the icon's shard off as soon as anything asks for an emoji. The span below
    // holds the final box in the meantime, so the row does not reflow when it lands.
    effect(() => {
      const ref = this.ref();
      if (ref && isNotoRef(ref)) void this.notoIcons.load(notoNameOf(ref));
    });
  }
}
